import React, { useEffect, useState } from "react";
import { AlertTriangle, Inbox, Mail, MessageSquare, Send, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { Panel } from "@/pages/shared";

const SECURITY = [
  { v: "ssl", label: "SSL/TLS (465)" },
  { v: "starttls", label: "STARTTLS (587)" },
  { v: "none", label: "Geen" },
];

export default function AdminEmail() {
  const { t } = useLang();
  const [cfg, setCfg] = useState(null);
  const [password, setPassword] = useState("");
  const [sms, setSms] = useState(null);
  const [token, setToken] = useState("");
  const [testNumber, setTestNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [imap, setImap] = useState(null);
  const [imapPw, setImapPw] = useState("");
  const [imapInfo, setImapInfo] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [m, s, i] = await Promise.all([api.get("/mail/smtp"), api.get("/sms/spryng"), api.get("/mailbox/settings")]);
        setCfg(m.data); setSms(s.data); setImap(i.data);
      } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    })();
  }, []);

  const saveSms = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/sms/spryng", { ...sms, api_token: token || undefined });
      setSms({ ...sms, ...data }); setToken(""); toast.success(t("saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const testSms = async () => {
    setBusy(true);
    try { await api.post("/sms/spryng/test", { recipient: testNumber }); toast.success(t("sms_test_ok")); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveImap = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/mailbox/settings", { ...imap, port: Number(imap.port), password: imapPw || undefined });
      setImap({ ...imap, ...data }); setImapPw(""); toast.success(t("saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const testImap = async () => {
    setBusy(true); setImapInfo(null);
    try { const { data } = await api.post("/mailbox/settings/test"); setImapInfo(data); toast.success(t("imap_test_ok")); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/mail/smtp", { ...cfg, port: Number(cfg.port), password: password || undefined });
      setCfg({ ...cfg, ...data });
      setPassword("");
      toast.success(t("saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true);
    try { await api.post("/mail/smtp/test"); toast.success(t("smtp_test_ok")); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!cfg || !sms) return <Panel title={t("admin_email")} testid="email-panel"><div className="p-5 text-sm text-muted-foreground">…</div></Panel>;

  const field = (key, label, extra = {}) => (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Input value={cfg[key] ?? ""} onChange={(e) => setCfg({ ...cfg, [key]: e.target.value })} data-testid={`smtp-${key}`} {...extra} />
    </div>
  );

  return (
    <Panel title={t("admin_email")} testid="email-panel"
      action={
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={test} disabled={busy || !cfg.host} data-testid="smtp-test-button"><ShieldCheck className="h-4 w-4 mr-1" />{t("smtp_test")}</Button>
          <Button onClick={save} disabled={busy || !cfg.host || !cfg.from_email} data-testid="smtp-save-button">{t("save")}</Button>
        </div>
      }
    >
      <div className="p-5 space-y-5 max-w-3xl">
        <p className="text-sm text-muted-foreground flex items-start gap-2">
          <Mail className="h-4 w-4 mt-0.5 shrink-0" />{t("admin_email_desc")}
        </p>

        {imap?.enabled && (!cfg.enabled || !cfg.host) && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-800" data-testid="mail-smtp-warning">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{t("mail_need_smtp_warn")}</span>
          </div>
        )}

        <div>
          <p className="font-display font-bold text-foreground flex items-center gap-2"><Send className="h-4 w-4" />{t("smtp_section_title")}</p>
          <p className="text-sm text-muted-foreground">{t("smtp_section_desc")}</p>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} data-testid="smtp-enabled" />
          {t("smtp_enabled")}
        </label>

        <div className="grid gap-4 md:grid-cols-2">
          {field("host", t("smtp_host"), { placeholder: "mail.mijnserver.be" })}
          {field("port", t("smtp_port"), { type: "number" })}
          <div className="space-y-1.5">
            <Label className="text-xs">{t("smtp_security")}</Label>
            <Select value={cfg.security || "ssl"} onValueChange={(v) => setCfg({ ...cfg, security: v })}>
              <SelectTrigger data-testid="smtp-security"><SelectValue /></SelectTrigger>
              <SelectContent>{SECURITY.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {field("username", t("smtp_username"))}
          {field("from_email", t("smtp_from_email"), { type: "email" })}
          {field("from_name", t("smtp_from_name"))}
          <div className="space-y-1.5">
            <Label className="text-xs flex items-center gap-2">
              {t("smtp_password")}
              {cfg.has_password && <span className="rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[10px] font-semibold">{t("smtp_password_set")}</span>}
            </Label>
            <Input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder={cfg.has_password ? "••••••••••••" : ""} data-testid="smtp-password" />
          </div>
        </div>

        <p className="text-[11px] text-muted-foreground flex items-start gap-2">
          <Send className="h-3.5 w-3.5 mt-0.5 shrink-0" />{t("smtp_fallback_hint")}
        </p>

        {/* Gedeelde mailbox via IMAP */}
        {imap && (
          <div className="border-t border-border pt-5 space-y-4" data-testid="imap-block">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="font-display font-bold text-foreground flex items-center gap-2"><Inbox className="h-4 w-4" />{t("imap_title")}</p>
                <p className="text-sm text-muted-foreground">{t("imap_desc")}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={testImap} disabled={busy || !imap.has_password} data-testid="imap-test-button"><ShieldCheck className="h-4 w-4 mr-1" />{t("smtp_test")}</Button>
                <Button onClick={saveImap} disabled={busy || !imap.host || !imap.username} data-testid="imap-save-button">{t("save")}</Button>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!imap.enabled} onChange={(e) => setImap({ ...imap, enabled: e.target.checked })} data-testid="imap-enabled" />
              {t("imap_enabled")}
            </label>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5"><Label className="text-xs">{t("imap_host")}</Label><Input value={imap.host ?? ""} onChange={(e) => setImap({ ...imap, host: e.target.value })} placeholder="mail.mijnserver.be" data-testid="imap-host" /></div>
              <div className="space-y-1.5"><Label className="text-xs">{t("smtp_port")}</Label><Input type="number" value={imap.port ?? 993} onChange={(e) => setImap({ ...imap, port: e.target.value })} data-testid="imap-port" /></div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("smtp_security")}</Label>
                <Select value={imap.security || "ssl"} onValueChange={(v) => setImap({ ...imap, security: v })}>
                  <SelectTrigger data-testid="imap-security"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ssl">SSL/TLS (993)</SelectItem>
                    <SelectItem value="starttls">STARTTLS (143)</SelectItem>
                    <SelectItem value="none">Geen</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">{t("smtp_username")}</Label><Input value={imap.username ?? ""} onChange={(e) => setImap({ ...imap, username: e.target.value })} data-testid="imap-username" /></div>
              <div className="space-y-1.5">
                <Label className="text-xs flex items-center gap-2">
                  {t("smtp_password")}
                  {imap.has_password && <span className="rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[10px] font-semibold">{t("smtp_password_set")}</span>}
                </Label>
                <Input type="password" autoComplete="new-password" value={imapPw} onChange={(e) => setImapPw(e.target.value)} placeholder={imap.has_password ? "••••••••••••" : ""} data-testid="imap-password" />
              </div>
              <div className="space-y-1.5"><Label className="text-xs">{t("imap_sent")}</Label><Input value={imap.sent_folder ?? ""} onChange={(e) => setImap({ ...imap, sent_folder: e.target.value })} data-testid="imap-sent" /></div>
              <div className="space-y-1.5"><Label className="text-xs">{t("imap_archive")}</Label><Input value={imap.archive_folder ?? ""} onChange={(e) => setImap({ ...imap, archive_folder: e.target.value })} data-testid="imap-archive" /></div>
              <div className="space-y-1.5"><Label className="text-xs">{t("imap_trash")}</Label><Input value={imap.trash_folder ?? ""} onChange={(e) => setImap({ ...imap, trash_folder: e.target.value })} data-testid="imap-trash" /></div>
            </div>
            {imapInfo && (
              <p className="text-xs text-muted-foreground" data-testid="imap-test-info">
                INBOX: {imapInfo.inbox_count} · {t("imap_folders")}: {imapInfo.folders?.join(", ")}
              </p>
            )}
          </div>
        )}

        {/* SMS via Spryng */}
        {sms && (
          <div className="border-t border-border pt-5 space-y-4" data-testid="spryng-block">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="font-display font-bold text-foreground flex items-center gap-2"><MessageSquare className="h-4 w-4" />{t("sms_title")}</p>
                <p className="text-sm text-muted-foreground">{t("sms_desc")}</p>
              </div>
              <div className="flex items-center gap-2">
                <Input value={testNumber} onChange={(e) => setTestNumber(e.target.value)} placeholder="32470123456" className="w-40" data-testid="sms-test-number" />
                <Button variant="outline" onClick={testSms} disabled={busy || !testNumber || !sms.has_token} data-testid="sms-test-button">{t("sms_test")}</Button>
                <Button onClick={saveSms} disabled={busy || !sms.originator || !sms.route} data-testid="sms-save-button">{t("save")}</Button>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!sms.enabled} onChange={(e) => setSms({ ...sms, enabled: e.target.checked })} data-testid="sms-enabled" />
              {t("sms_enabled")}
            </label>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs">{t("sms_originator")}</Label>
                <Input value={sms.originator ?? ""} onChange={(e) => setSms({ ...sms, originator: e.target.value })} data-testid="sms-originator" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("sms_route")}</Label>
                <Input value={sms.route ?? ""} onChange={(e) => setSms({ ...sms, route: e.target.value })} data-testid="sms-route" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs flex items-center gap-2">
                  {t("sms_token")}
                  {sms.has_token && <span className="rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[10px] font-semibold">{t("smtp_password_set")}</span>}
                </Label>
                <Input type="password" autoComplete="new-password" value={token} onChange={(e) => setToken(e.target.value)}
                  placeholder={sms.has_token ? "••••••••••••" : ""} data-testid="sms-token" />
              </div>
            </div>
          </div>
        )}
      </div>
    </Panel>
  );
}
